<template>
    <div class="group-category-tags" :class="{ 'group-category-tags--wrap': variant === 'wrap' }">
        <e-taglist v-if="tags.length" class="group-category-tags__list">
            <e-tag
                v-for="tag in visibleTags"
                :key="tag.id"
                v-tooltip.bottom="tableTooltip(tag)"
                :type="tagType"
                :size="tagSize"
                class="group-category-tag"
                :class="{ 'group-category-tag--table': variant === 'table' }"
                :title="variant === 'table' && hasNoHover ? tag.label : undefined"
                :role="variant === 'table' ? 'group' : undefined"
                :aria-label="variant === 'table' ? tag.label : undefined"
                :tabindex="variant === 'table' ? 0 : undefined"
                @focus="revealTableTooltip"
                @blur="dismissTableTooltip"
            >
                <span class="group-category-tag__content">
                    <strong v-if="tag.category" class="group-category-tag__category">{{ tag.category }}: </strong>
                    <span class="group-category-tag__names">{{ tag.names.join(', ') }}</span>
                </span>
            </e-tag>

            <Button
                v-if="overflowCount > 0"
                variant="text"
                size="small"
                :label="expanded ? 'Show less' : `+${overflowCount}`"
                @click="expanded = !expanded"
            />
        </e-taglist>
    </div>
</template>

<script>
import { useMediaQuery } from '@vueuse/core';

import { useHoverTooltipKeyboardAccess } from '@/composables/useHoverTooltipKeyboardAccess.js';
import { groupCategoryTagsFromGroups } from '@/utilities/groupCategoryLabels.js';

export default {
    name: 'GroupCategoryTags',

    setup() {
        const hasNoHover = useMediaQuery('(hover: none)');
        const { revealTooltip, dismissTooltip } = useHoverTooltipKeyboardAccess();
        return { hasNoHover, revealTooltip, dismissTooltip };
    },

    props: {
        groups: {
            type: Array,
            required: true,
        },

        variant: {
            type: String,
            default: 'wrap',
            validator: value => ['wrap', 'table'].includes(value),
        },

        maxVisibleTags: {
            type: Number,
            default: null,
        },

        tagType: {
            type: String,
            default: 'primary',
        },

        tagSize: {
            type: String,
            default: null,
        },
    },

    data: () => ({
        expanded: false,
    }),

    computed: {
        tags() {
            return groupCategoryTagsFromGroups(this.groups);
        },

        effectiveMaxVisibleTags() {
            if (this.variant === 'table' && this.maxVisibleTags == null) {
                return 2;
            }

            return this.maxVisibleTags;
        },

        visibleTags() {
            if (
                this.expanded ||
                this.effectiveMaxVisibleTags == null ||
                this.tags.length <= this.effectiveMaxVisibleTags
            ) {
                return this.tags;
            }

            return this.tags.slice(0, this.effectiveMaxVisibleTags);
        },

        overflowCount() {
            if (this.effectiveMaxVisibleTags == null) {
                return 0;
            }

            return Math.max(0, this.tags.length - this.effectiveMaxVisibleTags);
        },
    },

    methods: {
        revealTableTooltip(event) {
            if (this.variant === 'table') this.revealTooltip(event);
        },

        dismissTableTooltip(event) {
            if (this.variant === 'table') this.dismissTooltip(event);
        },

        tableTooltip(tag) {
            if (this.variant !== 'table') {
                return undefined;
            }

            return {
                value: tag.label,
                class: 'data-table-cell-tooltip',
            };
        },
    },
};
</script>

<style scoped lang="scss">
.group-category-tags__list {
    flex-wrap: wrap;
    min-width: 0;
    max-width: 100%;
}

.group-category-tags--wrap .group-category-tags__list {
    margin-bottom: 0 !important;
}

.group-category-tag {
    min-width: 0;
    max-width: 100%;
}

.group-category-tags--wrap .group-category-tag {
    height: auto;
    white-space: normal;
    overflow-wrap: anywhere;
}

.group-category-tag__content {
    display: inline;
    min-width: 0;
}

.group-category-tag__category {
    font-weight: 700;
}

.group-category-tag--table .group-category-tag__category + .group-category-tag__names {
    margin-left: 0.25em;
}

.group-category-tag--table {
    max-width: min(20rem, 100%);
}

.group-category-tag--table .group-category-tag__content {
    display: inline-flex;
    max-width: 100%;
    min-width: 0;
}

.group-category-tag--table .group-category-tag__category {
    flex: 0 1 auto;
    max-width: 50%;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.group-category-tag--table .group-category-tag__names {
    flex: 1 1 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
}
</style>
